import React, { FC, useEffect, useState } from "react";

import log from "loglevel";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { useParams } from "react-router-dom";

import NotAuthenticatedFormLayout from "@/components/layouts/NotAuthenticated/NotAuthenticatedFormLayout";
import { FlowSteps } from "@/components/molecules";
import { userApi } from "@/shared/apis";
import { RegisterState } from "@/shared/domain";
import { URL_EXPIRED } from "@/shared/errors";
import { useAuth, useDialog, useNavigation, useRouterHook } from "@/shared/hooks";

import * as tagManager from "../../../../tagManager";
import { RegisterSteps, stepperData } from "../Register/utils";
import RegisterDefinePassword from "./components/RegisterDefinePassword/RegisterDefinePassword";

const RegisterPassword: FC = () => {
    const {
        globalState: { lang, officeId },
    } = useRouterHook<RegisterState>();
    const [password, setPassword] = useState("");
    const { token } = useParams<string>();
    const { auth, loginSilent } = useAuth();
    const { t, i18n } = useTranslation();
    const { navToRoot } = useNavigation();
    const { errorDialog } = useDialog();

    const {
        isLoading: isLoadingTokenValidation,
        isError: isValidationTokenError,
        error: validationTokenError,
    } = useQuery(["getRegisterTokenValidate", { token }], () => userApi.getRegisterTokenValidate(token), {
        onSuccess: () => {
            log.info("validate token success");
        },
        enabled: !!token,
        useErrorBoundary: false,
    });

    useEffect(() => {
        if (isValidationTokenError) {
            //@ts-ignore
            log.warn("token validation failed", validationTokenError.message);
            errorDialog({
                //@ts-ignore
                errorCode: validationTokenError.code === "INVALID" ? URL_EXPIRED : "",
            });
            navToRoot(officeId);
        }
    }, [isValidationTokenError]);

    const { mutate: login, isLoading: isLoadingLogin } = useMutation(
        async (registerCompleteResponse: MyBrokerApi.RegistrationCompletedResponse) => {
            await auth(registerCompleteResponse.email, password);
            await loginSilent(registerCompleteResponse.email, registerCompleteResponse.officeId);
        },
        {
            onSuccess: () => {
                navToRoot();
            },
        },
    );

    const { mutate: registerComplete, isLoading: isLoadingRegister } = useMutation(userApi.registerComplete, {
        onSuccess: (registerCompleteResponse) => {
            tagManager.pushLoginEvent("email");
            log.info("register success");
            login(registerCompleteResponse);
        },
    });

    const handleDefinePassword = async (inputPassword: string) => {
        setPassword(inputPassword);
        registerComplete({
            body: { token, password: inputPassword },
        });
    };

    useEffect(() => {
        if (lang) i18n.changeLanguage(lang);

        if (!token) navToRoot();
    }, []);

    const isLoading = isLoadingRegister || isLoadingLogin || isLoadingTokenValidation;

    return (
        <>
            <NotAuthenticatedFormLayout
                officeId={officeId}
                title={t("PageTitles.Register")}
                flowStepsComponent={
                    <FlowSteps
                        title={t("Registration.Steps.Title")}
                        availableSteps={stepperData}
                        currentStep={RegisterSteps.CREATE_PASSWORD}
                    />
                }
            >
                <RegisterDefinePassword onDefinePassword={handleDefinePassword} isFetching={isLoading} />
            </NotAuthenticatedFormLayout>
        </>
    );
};

export default RegisterPassword;
